// ArticleContent.jsx - Renders article/landing page content with professional typography
import React from 'react';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Calendar, Clock, User, ChevronRight, TrendingUp, BookOpen, BarChart3, ArrowRight, Shield } from 'lucide-react';

// Hero style variants
const HeroStyles = {
  default: ({ title, subtitle, tag, author, formattedDate, readTime }) => (
    <header className="mb-10 sm:mb-14 pb-8 border-b border-gray-100">
      {tag && (
        <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-blue-50 text-blue-700 text-sm font-semibold rounded-full mb-5">
          <BookOpen size={13} />
          {tag}
        </span>
      )}
      <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-[#172A3A] leading-[1.15] mb-5">
        {title}
      </h1>
      {subtitle && (
        <p className="text-xl text-gray-500 leading-relaxed max-w-3xl">
          {subtitle}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-5 mt-7">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#172A3A] rounded-full flex items-center justify-center ring-2 ring-[#FFB800]/30">
            <span className="text-white font-bold text-sm">SF</span>
          </div>
          <div>
            <span className="text-sm font-semibold text-[#172A3A] block">{author}</span>
            <span className="text-xs text-gray-400">Medicare Retention Expert</span>
          </div>
        </div>
        <div className="hidden sm:block w-px h-8 bg-gray-200" />
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
          {formattedDate && (
            <span className="flex items-center gap-1.5">
              <Calendar size={14} className="text-[#FFB800]" />
              {formattedDate}
            </span>
          )}
          {readTime && (
            <span className="flex items-center gap-1.5">
              <Clock size={14} className="text-[#FFB800]" />
              {readTime} min read
            </span>
          )}
        </div>
      </div>
    </header>
  ),

  dark: ({ title, subtitle, tag, author, formattedDate, readTime }) => (
    <header className="mb-10 sm:mb-14 -mx-4 sm:-mx-6 px-6 sm:px-10 py-12 sm:py-16 bg-gradient-to-br from-[#172A3A] via-[#1e3a50] to-[#172A3A] text-white rounded-2xl relative overflow-hidden">
      <div className="absolute top-0 right-0 w-80 h-80 bg-[#FFB800]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
      <div className="relative">
        {tag && (
          <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-[#FFB800] text-[#172A3A] text-sm font-bold rounded-full mb-5">
            <BookOpen size={13} />
            {tag}
          </span>
        )}
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.12] mb-5">
          {title}
        </h1>
        {subtitle && (
          <p className="text-xl text-blue-100/80 leading-relaxed max-w-3xl">
            {subtitle}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-5 mt-8 pt-6 border-t border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FFB800] rounded-full flex items-center justify-center">
              <span className="text-[#172A3A] font-bold text-sm">SF</span>
            </div>
            <div>
              <span className="text-sm font-semibold text-white block">{author}</span>
              <span className="text-xs text-blue-200/60">Medicare Retention Expert</span>
            </div>
          </div>
          <div className="hidden sm:block w-px h-8 bg-white/20" />
          <div className="flex flex-wrap items-center gap-4 text-sm text-blue-200/70">
            {formattedDate && (
              <span className="flex items-center gap-1.5">
                <Calendar size={14} className="text-[#FFB800]" />
                {formattedDate}
              </span>
            )}
            {readTime && (
              <span className="flex items-center gap-1.5">
                <Clock size={14} className="text-[#FFB800]" />
                {readTime} min read
              </span>
            )}
          </div>
        </div>
      </div>
    </header>
  ),

  gradient: ({ title, subtitle, tag, author, formattedDate, readTime }) => (
    <header className="mb-10 sm:mb-14 -mx-4 sm:-mx-6 px-6 sm:px-10 py-12 sm:py-16 bg-gradient-to-br from-[#172A3A] via-[#1e3a50] to-[#2563eb] text-white rounded-2xl relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#FFB800]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-400/10 rounded-full blur-3xl" />
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.5) 1px, transparent 1px)',
        backgroundSize: '24px 24px'
      }} />
      <div className="relative">
        {tag && (
          <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-[#FFB800]/20 backdrop-blur-sm text-[#FFB800] text-sm font-semibold rounded-full mb-5">
            <BookOpen size={13} />
            {tag}
          </span>
        )}
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.12] mb-5">
          {title}
        </h1>
        {subtitle && (
          <p className="text-xl text-blue-100/80 leading-relaxed max-w-3xl">
            {subtitle}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-5 mt-8 pt-6 border-t border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FFB800] rounded-full flex items-center justify-center">
              <span className="text-[#172A3A] font-bold text-sm">SF</span>
            </div>
            <div>
              <span className="text-sm font-semibold text-white block">{author}</span>
              <span className="text-xs text-blue-200/60">Medicare Retention Expert</span>
            </div>
          </div>
          <div className="hidden sm:block w-px h-8 bg-white/20" />
          <div className="flex flex-wrap items-center gap-4 text-sm text-blue-200/70">
            {formattedDate && (
              <span className="flex items-center gap-1.5">
                <Calendar size={14} className="text-[#FFB800]" />
                {formattedDate}
              </span>
            )}
            {readTime && (
              <span className="flex items-center gap-1.5">
                <Clock size={14} className="text-[#FFB800]" />
                {readTime} min read
              </span>
            )}
          </div>
        </div>
      </div>
    </header>
  ),

  minimal: ({ title, subtitle, tag, author, formattedDate, readTime }) => (
    <header className="mb-10 sm:mb-14 text-center max-w-3xl mx-auto pb-8 border-b border-gray-100">
      {tag && (
        <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-gray-100 text-gray-600 text-sm font-semibold rounded-full mb-5">
          {tag}
        </span>
      )}
      <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-[#172A3A] leading-[1.15] mb-5">
        {title}
      </h1>
      {subtitle && (
        <p className="text-xl text-gray-500 leading-relaxed">
          {subtitle}
        </p>
      )}
      <div className="flex flex-wrap justify-center items-center gap-5 mt-7">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#172A3A] rounded-full flex items-center justify-center ring-2 ring-[#FFB800]/30">
            <span className="text-white font-bold text-sm">SF</span>
          </div>
          <span className="text-sm font-semibold text-[#172A3A]">{author}</span>
        </div>
        <div className="hidden sm:block w-px h-8 bg-gray-200" />
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
          {formattedDate && (
            <span className="flex items-center gap-1.5">
              <Calendar size={14} className="text-[#FFB800]" />
              {formattedDate}
            </span>
          )}
          {readTime && (
            <span className="flex items-center gap-1.5">
              <Clock size={14} className="text-[#FFB800]" />
              {readTime} min read
            </span>
          )}
        </div>
      </div>
    </header>
  ),

  stats: ({ title, subtitle, tag, author, formattedDate, readTime, stats }) => (
    <header className="mb-10 sm:mb-14 pb-8 border-b border-gray-100">
      {tag && (
        <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-purple-50 text-purple-700 text-sm font-semibold rounded-full mb-5">
          <BarChart3 size={13} />
          {tag}
        </span>
      )}
      <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-[#172A3A] leading-[1.15] mb-5">
        {title}
      </h1>
      {subtitle && (
        <p className="text-xl text-gray-500 leading-relaxed mb-8 max-w-3xl">
          {subtitle}
        </p>
      )}
      {stats && stats.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 my-8">
          {stats.map((stat, idx) => (
            <div key={idx} className={`p-5 rounded-xl text-center transition-all hover:scale-[1.02] ${
              stat.color === 'blue' ? 'bg-gradient-to-br from-blue-50 to-blue-100/50 border border-blue-200/50 text-blue-700' :
              stat.color === 'amber' ? 'bg-gradient-to-br from-amber-50 to-amber-100/50 border border-amber-200/50 text-amber-700' :
              stat.color === 'green' ? 'bg-gradient-to-br from-green-50 to-green-100/50 border border-green-200/50 text-green-700' :
              stat.color === 'red' ? 'bg-gradient-to-br from-red-50 to-red-100/50 border border-red-200/50 text-red-700' :
              'bg-gradient-to-br from-purple-50 to-purple-100/50 border border-purple-200/50 text-purple-700'
            }`}>
              <div className="text-2xl sm:text-3xl font-bold">{stat.number}</div>
              <div className="text-xs mt-1.5 font-medium opacity-80">{stat.label}</div>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-5 mt-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#172A3A] rounded-full flex items-center justify-center ring-2 ring-[#FFB800]/30">
            <span className="text-white font-bold text-sm">SF</span>
          </div>
          <div>
            <span className="text-sm font-semibold text-[#172A3A] block">{author}</span>
            <span className="text-xs text-gray-400">Medicare Retention Expert</span>
          </div>
        </div>
        <div className="hidden sm:block w-px h-8 bg-gray-200" />
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
          {formattedDate && (
            <span className="flex items-center gap-1.5">
              <Calendar size={14} className="text-[#FFB800]" />
              {formattedDate}
            </span>
          )}
          {readTime && (
            <span className="flex items-center gap-1.5">
              <Clock size={14} className="text-[#FFB800]" />
              {readTime} min read
            </span>
          )}
        </div>
      </div>
    </header>
  ),
};

export default function ArticleContent({
  title,
  subtitle,
  author = 'Scott Fluegel',
  publishedDate,
  readTime,
  content, // HTML string or React elements
  relatedLinks = [], // Array of {title, slug, description}
  sources = [], // Array of {title, url, publisher, accessedDate}
  showAuthorBio = true,
  heroStyle = 'default', // 'default', 'dark', 'gradient', 'minimal', 'stats'
  tag, // Optional category tag
  stats, // Optional stats array for 'stats' hero style
}) {
  const formattedDate = publishedDate
    ? new Date(publishedDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : null;

  const HeroComponent = HeroStyles[heroStyle] || HeroStyles.default;

  return (
    <article className="max-w-none">
      {/* Header - Now with variants */}
      <HeroComponent
        title={title}
        subtitle={subtitle}
        tag={tag}
        author={author}
        formattedDate={formattedDate}
        readTime={readTime}
        stats={stats}
      />

      {/* Main Content - Enhanced professional typography */}
      <div className="
        prose prose-lg max-w-none
        prose-headings:text-[#172A3A] prose-headings:font-bold
        prose-h2:text-2xl prose-h2:sm:text-[1.65rem] prose-h2:mt-12 prose-h2:mb-4 prose-h2:pb-3 prose-h2:border-b prose-h2:border-gray-100
        prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-h3:text-[#1e3a50]
        prose-p:text-gray-700 prose-p:leading-[1.8] prose-p:text-[1.05rem]
        prose-a:text-blue-600 prose-a:no-underline prose-a:font-medium hover:prose-a:underline hover:prose-a:text-blue-700
        prose-strong:text-[#172A3A] prose-strong:font-semibold
        prose-li:text-gray-700 prose-li:leading-[1.8] prose-li:text-[1.05rem]
        prose-li:marker:text-[#FFB800]
        prose-ul:my-6 prose-ol:my-6
        prose-blockquote:border-l-4 prose-blockquote:border-l-[#FFB800] prose-blockquote:bg-gradient-to-r prose-blockquote:from-amber-50 prose-blockquote:to-transparent prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:rounded-r-xl prose-blockquote:not-italic prose-blockquote:text-gray-700 prose-blockquote:my-8 prose-blockquote:shadow-sm
        prose-em:text-gray-600
        prose-img:rounded-xl prose-img:shadow-md
      ">
        {typeof content === 'string' ? (
          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }} />
        ) : (
          content
        )}
      </div>

      {/* Related Reading - Enhanced cards */}
      {relatedLinks.length > 0 && (
        <div className="mt-14 pt-10 border-t border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <BookOpen size={16} className="text-blue-600" />
            </div>
            <h3 className="text-lg font-bold text-[#172A3A]">Continue Reading</h3>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {relatedLinks.map((link, idx) => (
              <Link
                key={idx}
                to={link.slug.startsWith('/') ? link.slug : `/learn/${link.slug}`}
                className="group block p-5 rounded-xl border border-gray-200 hover:border-[#FFB800]/50 hover:shadow-md transition-all duration-300 bg-white hover:-translate-y-0.5"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <h4 className="font-semibold text-[#172A3A] group-hover:text-[#172A3A] leading-snug mb-1.5">
                      {link.title}
                    </h4>
                    {link.description && (
                      <p className="text-sm text-gray-500 leading-relaxed">{link.description}</p>
                    )}
                  </div>
                  <div className="w-8 h-8 rounded-lg bg-gray-50 group-hover:bg-[#FFB800]/10 flex items-center justify-center flex-shrink-0 transition-colors">
                    <ArrowRight size={14} className="text-gray-400 group-hover:text-[#FFB800] transition-colors" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Sources - Cleaner design */}
      {sources.length > 0 && (
        <div className="mt-10 pt-8 border-t border-gray-100">
          <details className="group" open>
            <summary className="flex items-center gap-2 text-sm font-semibold text-gray-500 cursor-pointer hover:text-gray-700 transition-colors">
              <Shield size={14} />
              Sources & References ({sources.length})
            </summary>
            <ol className="mt-4 space-y-2 text-sm text-gray-500 list-decimal list-inside">
              {sources.map((source, idx) => (
                <li key={idx} className="pl-1">
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-blue-600 transition-colors"
                  >
                    {source.title}
                  </a>
                  {source.publisher && <span className="text-gray-400"> — {source.publisher}</span>}
                  {source.accessedDate && <span className="text-gray-300"> (accessed {source.accessedDate})</span>}
                </li>
              ))}
            </ol>
          </details>
        </div>
      )}

      {/* Author Bio - Professional design */}
      {showAuthorBio && (
        <div className="mt-14 p-8 bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-start gap-5">
            <div className="w-16 h-16 bg-[#172A3A] rounded-2xl flex items-center justify-center flex-shrink-0 ring-2 ring-[#FFB800]/20">
              <span className="text-white font-bold text-xl">SF</span>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h4 className="font-bold text-[#172A3A] text-lg">Scott Fluegel</h4>
                <span className="px-2.5 py-0.5 bg-[#FFB800]/10 text-[#172A3A] text-xs font-semibold rounded-full">Founder, Pro IRP</span>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                Scott is a Medicare industry veteran and the founder of{' '}
                <Link to="/" className="text-blue-600 hover:underline font-medium">Pro IRP</Link>,
                a client retention platform built specifically for Medicare agents and agencies.
                After years of watching good agents lose clients they shouldn't have lost,
                he's building the tools he wished he had.
              </p>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}
